import React, { useState } from 'react';
import { X, FileText, Image as ImageIcon } from 'lucide-react';

/**
 * DownloadFormatModal Component
 * Allows users to choose between PDF or Image format before downloading a document
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when closing the modal
 * @param {function} onSelectFormat - Function to call with selected format ('pdf' or 'image')
 * @param {string} documentName - Name of the document being downloaded
 */
const DownloadFormatModal = ({ isOpen, onClose, onSelectFormat, documentName = 'Document' }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onSelectFormat(selectedFormat);
    onClose();
  };

  const formats = [
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Portable Document Format - Best for printing and official records',
      icon: FileText,
      extension: '.pdf',
      color: 'violet'
    },
    {
      id: 'image',
      name: 'Image',
      description: 'PNG Image - Easy to share on messaging apps',
      icon: ImageIcon,
      extension: '.png',
      color: 'emerald'
    }
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Choose Download Format</h2>
            <p className="text-sm text-gray-600 mt-0.5">{documentName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Format Options */}
        <div className="p-4 space-y-3">
          {formats.map((format) => {
            const Icon = format.icon;
            const isSelected = selectedFormat === format.id;
            const borderColor = isSelected 
              ? format.color === 'violet' ? 'border-violet-500' : 'border-emerald-500'
              : 'border-gray-200';
            const bgColor = isSelected
              ? format.color === 'violet' ? 'bg-violet-50' : 'bg-emerald-50'
              : 'bg-white';

            return (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`w-full p-4 border-2 ${borderColor} ${bgColor} rounded-lg hover:shadow-md transition-all text-left`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    format.color === 'violet' ? 'bg-violet-100' : 'bg-emerald-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      format.color === 'violet' ? 'text-violet-600' : 'text-emerald-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{format.name}</h3>
                      <span className="text-xs text-gray-500">{format.extension}</span>
                      {isSelected && (
                        <div className={`ml-auto w-5 h-5 rounded-full flex items-center justify-center ${
                          format.color === 'violet' ? 'bg-violet-500' : 'bg-emerald-500'
                        }`}>
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{format.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium"
            >
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadFormatModal;
